import type { PlanGeneralForm } from '../schemas/parameters.schema';
import type {
	FertilizationPlanReferenceDTO,
	FertilizationPlanReferencePayload,
	ReferenceFormValue, StateReferenceDTO,
	StateReferencePayload,
} from '../types/parameters.types';

const stripIds = <T>(value: T): T => JSON.parse(JSON.stringify(value, (key, item) => (key === '_id' ? undefined : item)));

export const buildReferencePayload = (
	form: ReferenceFormValue,
	base: StateReferenceDTO | undefined
): StateReferencePayload => ({
	stateCode: form.stateCodes,
	referenceCode: form.name.trim(),
	description: '',
	dependentParams: stripIds(base?.dependentParams ?? []),
	fixedParams: stripIds(base?.fixedParams ?? []),
	demandP2O5: stripIds(base?.demandP2O5 ?? []),
	liming: stripIds(base?.liming ?? []),
	plastering: stripIds(base?.plastering ?? []),
	criticalRanges: stripIds(base?.criticalRanges ?? []),
	drisParams: stripIds(base?.drisParams ?? []),
});

export const normalizeReference = (reference: StateReferenceDTO): StateReferenceDTO => ({
	...reference,
	description: reference.description ?? '',
	dependentParams: reference.dependentParams ?? [],
	fixedParams: reference.fixedParams ?? [],
	demandP2O5: reference.demandP2O5 ?? [],
	liming: reference.liming ?? [],
	plastering: reference.plastering ?? [],
	criticalRanges: reference.criticalRanges ?? [],
	drisParams: reference.drisParams ?? [],
});

const generatePlanKey = (name: string, plans: FertilizationPlanReferenceDTO[]) => {
	const slug =
		name
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '') || 'plano';
	const usedKeys = new Set(plans.map((item) => item.key));
	if (!usedKeys.has(slug)) return slug;
	let suffix = 2;
	while (usedKeys.has(`${slug}-${suffix}`)) suffix += 1;
	return `${slug}-${suffix}`;
};

export const buildPlanPayload = (
	base: FertilizationPlanReferenceDTO,
	form: PlanGeneralForm,
	plans: FertilizationPlanReferenceDTO[]
): FertilizationPlanReferencePayload => {
	const { _id, createdAt, updatedAt, __v, ...coefficients } = base as FertilizationPlanReferenceDTO &
		Record<'createdAt' | 'updatedAt' | '__v', unknown>;
	return { ...coefficients, ...form, key: generatePlanKey(form.name, plans), isActive: true };
};
